import './CompensationRequestForm.scss';
import { Button } from '@mui/material';
import { CompensationVoucher, ValidationError } from '../../models/home';
import { Dispatch, memo, SetStateAction } from 'react';
import { NOTIFICATION_TYPE } from '../../constants/common';
import { useCompensationRequestForm } from './useCompensationRequestForm';
import AppLoader from '../AppCommon/AppLoader/AppLoader';
import AppNotification from '../AppCommon/AppNotification/AppNotification';
import BookingInformation from '../../components/BookingInformation/BookingInformation';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import Compensation from '../../components/Compensation/Compensation';
import FlightInformation from '../FlightInformation/FlightInformation';
import PassengerInformation from '../../components/PassengerInformation/PassengerInformation';
import { AppNotifications } from '../AppNotifications/AppNotifications';

interface Props {
    compensationVouchers: CompensationVoucher[];
    isDeviceOffline: boolean;
    validationError: ValidationError;
    setCompensationVouchers: Dispatch<SetStateAction<CompensationVoucher[]>>;
    setShowConfirmation: Dispatch<SetStateAction<boolean>>;
    setValidationError: Dispatch<SetStateAction<ValidationError>>;
}

const CompensationRequestForm = ({
    compensationVouchers,
    isDeviceOffline,
    validationError,
    setCompensationVouchers,
    setShowConfirmation,
    setValidationError,
}: Props) => {
    const {
        bookingInformationFields,
        compensationFields,
        flightInformationFields,
        isFlightUser,
        loadingRetry,
        loadingMutation,
        loadingVoucherCodes,
        loadingOfflineModule,
        passengerInformationFields,
        resendErroredVoucherId,
        showFlightNumberPlaceholder,
        submitButtonDisabled,
        handleOnClickClear,
        handleOnClickSubmit,
        setBookingInformationFields,
        setCompensationFields,
        setFlightInformationFields,
        setPassengerInformationFields,
        setShowFlightNumberPlaceholder,
        t
    } = useCompensationRequestForm({
        compensationVouchers,
        isDeviceOffline,
        setCompensationVouchers,
        setShowConfirmation,
        setValidationError
    });

    return (
        loadingRetry
        || loadingMutation
        || loadingVoucherCodes
        || loadingOfflineModule ? (
            <AppLoader />
        ) : (
            <div className="c-request-form">
                <AppNotifications style={{ 
                    marginTop: '3rem',
                }}/>
                {isDeviceOffline && (
                    <div className="c-notification">
                        <AppNotification message={t('notifications.offline')} type={NOTIFICATION_TYPE.neutral} />
                    </div>
                )}
                {(!validationError.isError && compensationVouchers.length > 0 && compensationVouchers[0].type === 'Wi-Fi' && !loadingVoucherCodes) && (
                    <div className="c-notification">
                        <AppNotification 
                            message={
                                compensationVouchers[0].code
                                    ? `${t('notifications.success')}\n${t('notifications.voucherCodes')}: ${compensationVouchers.map(v => v.code).join(', ')}`
                                    : t('notifications.success')
                            }
                            type={NOTIFICATION_TYPE.success}
                        />
                    </div>
                )}
                {validationError.isError && (
                    <div className="c-notification">
                        <AppNotification 
                            message={
                                validationError.errorCode === 'partial_success' 
                                    ? `${validationError.successCount} ${t('notifications.partial_success_first')} ${validationError.failureCount} ${t('notifications.partial_success_second')}`
                                    : t('notifications.' + (typeof validationError.errorCode === 'number' ? 
                                        validationError.errorCode.toString() : 
                                        validationError.errorCode))
                            } 
                            type={NOTIFICATION_TYPE.error} 
                        />
                    </div>
                )}
                <div className="c-row">
                    <div className="c-col">
                        <h2 className="h-subtitle">{t('titles.flightInformation')}</h2>
                        <FlightInformation
                            fields={flightInformationFields}
                            showPlaceholder={showFlightNumberPlaceholder}
                            setFields={setFlightInformationFields}
                            setShowPlaceholder={setShowFlightNumberPlaceholder}
                        />
                    </div>
                    <div className="c-col">
                        <h2 className="h-subtitle h-subtitle--pl">{t('titles.bookingInformation')}</h2>
                        <BookingInformation
                            fields={bookingInformationFields}
                            setFields={setBookingInformationFields} 
                            setCompensationFields={setCompensationFields}
                        />
                    </div>
                </div>

                <h2 className="h-subtitle h-subtitle--mt">{t('titles.passengerInformation')}</h2>
                <PassengerInformation fields={passengerInformationFields} setFields={setPassengerInformationFields} />

                <h2 className="h-subtitle h-subtitle--compensation">{t('titles.compensation')}</h2>
                <Compensation fields={compensationFields} setFields={setCompensationFields} />
                
                <div className="c-btn">
                    <Button className="btn btn--secondary btn-clear" onClick={() => handleOnClickClear()}>
                        {t('buttons.clearForm')}
                    </Button>
                    <Button
                        className="btn btn--primary btn-submit"
                        disabled={submitButtonDisabled}
                        onClick={handleOnClickSubmit}
                    >
                        {resendErroredVoucherId ? t('buttons.resendCompensation') : t('buttons.submit')}
                    </Button>
                </div>
            </div>  
        )    
    );
};

export default memo(CompensationRequestForm);

